import { useEffect, useState } from 'react';

import {
  buildSnapshotColumn,
  planCalendarSnapshot,
  type SnapshotColumn,
  type TimeSpan,
} from './calendarSnapshot';
import { useCalendarSnapshotEventsQueries } from './calendarSnapshot.query';
import { parseDateValue } from './dateValue';

const NOW_TICK_MS = 60_000;

export type CalendarSnapshotView = {
  hourMarks: Date[];
  columns: SnapshotColumn[];
};

const spanOf = (
  start: string | null | undefined,
  end: string | null | undefined
): TimeSpan | null => {
  const startDate = parseDateValue(start ?? undefined);
  const endDate = parseDateValue(end ?? undefined);
  return startDate && endDate ? { start: startDate, end: endDate } : null;
};

// While a column's events load, or after they fail, it shows only the proposed and
// previous slots
export const useCalendarSnapshot = ({
  start,
  end,
  title,
  previousStart,
  previousEnd,
  replacesEventId,
  allDay,
}: {
  start: string;
  end: string;
  title: string | null | undefined;
  previousStart: string | null | undefined;
  previousEnd: string | null | undefined;
  replacesEventId: string | null | undefined;
  allDay: boolean;
}): CalendarSnapshotView | null => {
  const [nowMs, setNowMs] = useState(Date.now);
  useEffect(() => {
    const timer = setInterval(() => setNowMs(Date.now()), NOW_TICK_MS);
    return () => clearInterval(timer);
  }, []);

  const proposed = allDay ? null : spanOf(start, end);
  const previous = spanOf(previousStart, previousEnd);
  const plan = proposed
    ? planCalendarSnapshot({ proposed, previous, nowMs })
    : null;
  const queries = useCalendarSnapshotEventsQueries(
    plan?.columns.map((column) => column.fetch) ?? []
  );

  if (!proposed || !plan) return null;
  return {
    hourMarks: plan.hourMarks,
    columns: plan.columns.map((column, index) => {
      const query = queries[index];
      return buildSnapshotColumn({
        column,
        proposed: { ...proposed, title: title ?? '' },
        previous,
        events: query?.isSuccess ? query.data.events : [],
        excludedEventId: replacesEventId ?? null,
        nowMs,
      });
    }),
  };
};
